/**
 * What an extracted criterion's readings are worth — Story 20.8, DECISION-143.
 *
 * PRD §3.6: "Extracted values scored by the same weight engine as dropdowns." The
 * values always entered the engine; until this story every extracted criterion an
 * operator added scored zero, because nothing could edit its map (Q-103).
 *
 * THE FIELD'S TYPE PICKS THE SHAPE, AND THE OPERATOR ENTERS EVERY NUMBER. Each
 * `ScoringMap` kind already exists and `award` in `packages/schema` already
 * honours it, so this module decides which kind a TYPE gets and never which value
 * is good:
 *
 *   One of   `options`, one row per value, then Nothing read
 *   Yes/no   `options` keyed `true` and `false`, then Nothing read — never
 *            `presence`, which awards a "no" what a "yes" gets (DECISION-142)
 *   Number   `ranges`, first match wins, then Nothing read
 *   Text     `presence`: Mentioned, and Not mentioned
 *   Date     the same two rows
 *
 * EVERY ROW SHOWS WHAT THE STORED MAP AWARDS, asked of the scorer itself. A map
 * written before this story, or by the API, is not rewritten to be shown: 20.3's
 * empty `options` map on a Number field reads as one range scoring 0, and the
 * archived B2B copies' `presence` map on a Yes/no field reads as Yes and No
 * scoring the same — which is the truth, and the defect, on the screen. The first
 * change writes the field's own shape carrying every other row's points, so
 * scoring moves only where the operator moved it.
 *
 * NOTHING HERE IS A SCHEMA REFUSAL, on purpose. Drafts carrying a `presence` map
 * on a Yes/no field exist, and a draft that stops parsing is the curated-blueprint
 * outage's class (`fe2ebed`). The editor declines to OFFER a wrong shape; it does
 * not make one unreadable.
 */
import {
  DERIVED_READS,
  applyOperation,
  award,
  emptyPointsFor,
  maxPoints,
  pointsArgsOf,
  type Criterion,
  type ExtractionField,
  type Funnel,
  type Reads,
  type ScoringMap,
} from '@leadgible/schema'
import { COMPOSED, DRAWN, ORDINALS } from './extracted-points-copy'
import { joinNames } from './extraction'
import { readingOf } from './vocabulary'

/**
 * What a criterion reads, as far as its points care (`Reads`: a type, and a
 * Number's bounds or a One of's values); the ICP fit, scored as a Number field is
 * over 0–100 (`DERIVED_READS`, DECISION-138); and a new criterion's points in its
 * reading's shape, at zero (`emptyPointsFor`, DECISION-143). The vocabulary's own
 * since Story 27.2 — `set_criterion` writes a new criterion's points with them —
 * and re-exported where this module's readers found them.
 */
export { DERIVED_READS, emptyPointsFor, type Reads }

/** A criterion whose points this module edits: an extracted field, or — since Story 20.9 — the ICP fit. */
export type Scored = Extract<Criterion, { source: 'extracted' | 'derived' }>
type FieldType = ExtractionField['type']

/** What this criterion reads, or undefined when it reads nothing this module can score. */
export function readsOf(funnel: Funnel, criterion: Criterion): Reads | undefined {
  if (criterion.source === 'derived') return DERIVED_READS[criterion.key]
  if (criterion.source !== 'extracted') return undefined
  return funnel.rubric.extraction.find((field) => field.field_key === criterion.field_key)
}

/** The four shapes DECISION-143 gives the five types. */
export type Shape = 'values' | 'yes_no' | 'ranges' | 'mention'

export const SHAPE: Record<FieldType, Shape> = {
  enum: 'values',
  boolean: 'yes_no',
  number: 'ranges',
  string: 'mention',
  date: 'mention',
}

/** The map kind each shape writes. A Yes/no is keyed like a One of, never `presence`. */
export const KIND: Record<Shape, ScoringMap['kind']> = {
  values: 'options',
  yes_no: 'options',
  ranges: 'ranges',
  mention: 'presence',
}

/** The keys a Yes/no's `options` map uses: `award` looks a boolean up by `String(value)`. */
const YES_NO = ['true', 'false'] as const

/** `"5"` is a key a reading of 5 matches; `"05"` and `"five"` are keys no number ever does. */
const isNumberKey = (key: string): boolean => key.trim() !== '' && String(Number(key)) === key

/**
 * The map in this field's shape that awards exactly what `map` awards for every
 * reading the field can give — asked of `award`, so no second opinion on what a
 * map means exists here.
 *
 * Always a fresh object, so a recipe can change it without touching the one it
 * came from. What the shape cannot carry is what `strandedKeys` names.
 */
export function fitted(field: Reads, map: ScoringMap): ScoringMap {
  const nothing = award(map, undefined)
  switch (SHAPE[field.type]) {
    case 'values':
      return {
        kind: 'options',
        points_by_option: Object.fromEntries(
          (field.enum_values ?? []).map((value) => [value, award(map, value)]),
        ),
        default_points: nothing,
      }
    case 'yes_no':
      return {
        kind: 'options',
        points_by_option: { true: award(map, true), false: award(map, false) },
        default_points: nothing,
      }
    case 'ranges': {
      if (map.kind === 'ranges') {
        return { kind: 'ranges', ranges: map.ranges.map((range) => ({ ...range })), default_points: nothing }
      }
      // An `options` map keyed on numbers already scores those exact numbers,
      // each carried as a range of one.
      const exact =
        map.kind === 'options'
          ? Object.entries(map.points_by_option).flatMap(([key, points]) =>
              isNumberKey(key) ? [{ min: Number(key), max: Number(key), points }] : [],
            )
          : []
      if (exact.length > 0) return { kind: 'ranges', ranges: exact, default_points: nothing }
      // Otherwise every number it reads scores alike: `presence` awards its
      // present points to any number, and `options` its default.
      return {
        kind: 'ranges',
        ranges: [
          {
            min: field.min ?? null,
            max: field.max ?? null,
            points: map.kind === 'presence' ? map.points_present : map.default_points,
          },
        ],
        default_points: nothing,
      }
    }
    case 'mention':
      if (map.kind === 'presence') return { ...map }
      // Anything read counts as mentioned. An `options` key matching a whole
      // reading word for word is what `strandedKeys` names; a `keywords` map —
      // PRD §3.6's `rules` mode, which no writer the product has puts on an
      // extracted criterion — reads as its default here.
      return { kind: 'presence', points_present: map.default_points, points_absent: nothing }
  }
}

/**
 * Points a field can no longer award: `options` keys no reading of this field
 * matches, holding points other than the map's default. DECISION-067's shape —
 * "the option scores zero and nothing reports it" — reached by a value taken off
 * a One of, or a field whose type changed underneath its criterion.
 *
 * A key worth only the default loses nothing when it goes, so it is not named.
 */
export function strandedKeys(field: Reads, map: ScoringMap): string[] {
  if (map.kind !== 'options') return []
  const matters = ([, points]: [string, number]) => points !== map.default_points
  const entries = Object.entries(map.points_by_option).filter(matters)
  switch (SHAPE[field.type]) {
    case 'values': {
      const values = new Set(field.enum_values ?? [])
      return entries.filter(([key]) => !values.has(key)).map(([key]) => key)
    }
    case 'yes_no':
      return entries.filter(([key]) => !(YES_NO as readonly string[]).includes(key)).map(([key]) => key)
    case 'ranges':
      return entries.filter(([key]) => !isNumberKey(key)).map(([key]) => key)
    case 'mention':
      return entries.map(([key]) => key)
  }
}

/** The sentence under the heading when `strandedKeys` names anything. */
export function strandedLine(keys: readonly string[]): string | null {
  if (keys.length === 0) return null
  if (keys.length === 1) return COMPOSED.strandedOne(keys[0]!)
  return COMPOSED.strandedMany(joinNames([...keys]))
}

/* ── what the panel renders ─────────────────────────────────────────────── */

export type ValueRow = {
  /** The key the row writes: an enum value, or `true` / `false`. */
  key: string
  label: string
  /** The score input's name, as the capture writes it. */
  name: string
  points: number
}

export type RangeRow = {
  min: number | null
  max: number | null
  points: number
  fromName: string
  toName: string
  scoreName: string
  /** A start above its end, which no number falls in. */
  backwards: boolean
}

export type PointsView =
  | { shape: 'values' | 'yes_no'; rows: ValueRow[]; nothing: number; stranded: string[] }
  | { shape: 'ranges'; ranges: RangeRow[]; nothing: number; stranded: string[] }
  | { shape: 'mention'; present: number; absent: number; stranded: string[] }

export const ordinal = (index: number): string => ORDINALS[index] ?? COMPOSED.ordinal(index + 1)

/** The score input's name for a range, from its own ends. */
export function rangeScoreName(min: number | null, max: number | null): string {
  if (min !== null && max !== null) return DRAWN.scoreBetween(min, max)
  if (min !== null) return DRAWN.scoreUpwards(min)
  if (max !== null) return COMPOSED.scoreUpTo(max)
  return COMPOSED.scoreAny
}

/** The rows for this field's type, each worth what the stored map awards. */
export function pointsView(field: Reads, map: ScoringMap): PointsView {
  const shaped = fitted(field, map)
  const stranded = strandedKeys(field, map)
  switch (SHAPE[field.type]) {
    case 'values':
    case 'yes_no': {
      if (shaped.kind !== 'options') throw new Error('unreachable: a keyed shape is an options map')
      const shape = SHAPE[field.type] as 'values' | 'yes_no'
      const readings =
        shape === 'yes_no'
          ? [
              { key: 'true', label: DRAWN.yes },
              { key: 'false', label: DRAWN.no },
            ]
          : (field.enum_values ?? []).map((value) => ({ key: value, label: value }))
      return {
        shape,
        rows: readings.map(({ key, label }) => ({
          key,
          label,
          name: DRAWN.scoreFor(label),
          points: shaped.points_by_option[key] ?? shaped.default_points,
        })),
        nothing: shaped.default_points,
        stranded,
      }
    }
    case 'ranges': {
      if (shaped.kind !== 'ranges') throw new Error('unreachable: a Number is a ranges map')
      return {
        shape: 'ranges',
        ranges: shaped.ranges.map((range, index) => ({
          min: range.min,
          max: range.max,
          points: range.points,
          fromName: DRAWN.rangeFrom(ordinal(index)),
          toName: DRAWN.rangeTo(ordinal(index)),
          scoreName: rangeScoreName(range.min, range.max),
          backwards: range.min !== null && range.max !== null && range.min > range.max,
        })),
        nothing: shaped.default_points,
        stranded,
      }
    }
    case 'mention': {
      if (shaped.kind !== 'presence') throw new Error('unreachable: a Text or Date is a presence map')
      return { shape: 'mention', present: shaped.points_present, absent: shaped.points_absent, stranded }
    }
  }
}

/* ── the recipes ────────────────────────────────────────────────────────── */

/** Which row an edit is for. */
export type Target =
  | { row: 'value'; key: string }
  | { row: 'nothing' }
  | { row: 'present' }
  | { row: 'absent' }
  | { row: 'range'; index: number }

/** A criterion this module edits and what it reads, or null for anything else. */
function locate(draft: Funnel, criterionId: string): { criterion: Scored; field: Reads } | null {
  const criterion = draft.rubric.criteria.find((c) => c.id === criterionId)
  if (criterion === undefined || (criterion.source !== 'extracted' && criterion.source !== 'derived')) return null
  const field = readsOf(draft, criterion)
  return field === undefined ? null : { criterion, field }
}

/**
 * Write a new map, unless it says what the old one said — through the
 * vocabulary's `set_criterion` since Story 27.2.
 *
 * `weight` is the criterion's maximum and the rubric refuses any points above it
 * (`packages/schema/src/rubric.ts`), so a score past the weight raises the weight
 * with it — `setOptionPoints`' rule on the answer path, one tab over, and the
 * operation's own when it is not told a weight. The comparison stays here: the
 * operation writes the map it is given, and a new object equal to the old one is
 * still a patch, which would be an undo step that undoes nothing.
 */
function commit(draft: Funnel, criterion: Scored, next: ScoringMap): void {
  const weight = Math.max(criterion.weight, maxPoints(next))
  if (weight === criterion.weight && JSON.stringify(next) === JSON.stringify(criterion.points)) return
  const reading = readingOf(criterion)
  if (reading === null) return
  applyOperation(draft, { op: 'set_criterion', id: criterion.id, ...reading, points: pointsArgsOf(next) })
}

/**
 * Set one row's points, as a recipe for `edit()`.
 *
 * The map is put in its field's shape first, carrying every other row's points,
 * so an edit to one row never moves another. A key the field does not read, a
 * row the shape does not have, or a number the rubric cannot hold changes nothing.
 */
export function setPoints(draft: Funnel, criterionId: string, target: Target, points: number): void {
  if (!Number.isInteger(points) || points < 0) return
  const found = locate(draft, criterionId)
  if (found === null) return
  const next = fitted(found.field, found.criterion.points)

  switch (target.row) {
    case 'value':
      // An own key only: `in` would also say yes to `toString`.
      if (next.kind !== 'options' || !Object.hasOwn(next.points_by_option, target.key)) return
      next.points_by_option[target.key] = points
      break
    case 'nothing':
      if (next.kind !== 'options' && next.kind !== 'ranges') return
      next.default_points = points
      break
    case 'present':
      if (next.kind !== 'presence') return
      next.points_present = points
      break
    case 'absent':
      if (next.kind !== 'presence') return
      next.points_absent = points
      break
    case 'range': {
      const range = next.kind === 'ranges' ? next.ranges[target.index] : undefined
      if (range === undefined) return
      range.points = points
      break
    }
  }
  commit(draft, found.criterion, next)
}

/**
 * Set one end of a range. `null` is no limit on that side. A finite number is
 * the only other thing a bound can be.
 */
export function setRangeBound(
  draft: Funnel,
  criterionId: string,
  index: number,
  end: 'min' | 'max',
  value: number | null,
): void {
  if (value !== null && !Number.isFinite(value)) return
  const found = locate(draft, criterionId)
  if (found === null || SHAPE[found.field.type] !== 'ranges') return
  const next = fitted(found.field, found.criterion.points)
  const range = next.kind === 'ranges' ? next.ranges[index] : undefined
  if (range === undefined) return
  range[end] = value
  commit(draft, found.criterion, next)
}

/**
 * Add a range at the end, starting where the last one stops, open above and
 * worth nothing until the operator says otherwise. Ranges are checked in order,
 * so it only ever takes numbers the ranges before it leave.
 */
export function addRange(draft: Funnel, criterionId: string): void {
  const found = locate(draft, criterionId)
  if (found === null || SHAPE[found.field.type] !== 'ranges') return
  const next = fitted(found.field, found.criterion.points)
  if (next.kind !== 'ranges') return
  const last = next.ranges[next.ranges.length - 1]
  next.ranges.push({ min: last?.max ?? last?.min ?? null, max: null, points: 0 })
  commit(draft, found.criterion, next)
}
